import {
  applyLocationScope,
  ChartFilters,
  NO_MATCH_LOCATION_ID,
} from "hooks/useAdminGroup/applyLocationScope";

/**
 * Adds the breadcrumb scope as a separate `filters.scope_locations` key -- the
 * server-enforced channel. `NextosReportBase._resolve_locations` intersects it
 * with the role clamp, fails closed to the no-match sentinel, and rides the
 * result in `base_params['locations']`, which `CDRQuery._base_search` ANDs
 * unconditionally.
 *
 * The `scope_locations` counterpart to `applyLocationScope`, for filters that
 * did NOT originate on this client -- a saved report definition echoed back by
 * the server, for example. One behavioural difference, and it is the whole
 * reason this exists separately: it never INVENTS a `locations` key.
 * `locations` is an entity term filter, and `CDRQuery._term_filters` ORs all
 * entity type_filters together once "teams" is present, so seeding one into a
 * teams-shaped saved report would OR-WIDEN the result instead of narrowing it.
 * A `locations` filter that is already there is still intersected -- delegated
 * to `applyLocationScope` so the intersection, the id normalisation and the
 * fail-closed sentinel live in exactly one place -- so a saved location filter
 * can only narrow further inside the scope, never escape it.
 *
 * `scope_locations` is inert to bucket inference and to `_term_filters` (which
 * matches on an explicit allowlist of entity keys), so adding it cannot change
 * what the report is bucketed by.
 *
 * Returns `filters` unchanged -- the same reference -- when no scope applies,
 * so callers that diff filters by reference do not refetch.
 */
export const addScopeLocationsFilter = (
  filters: ChartFilters,
  applyScope: boolean,
  scopeExternalIds: Set<string>,
): ChartFilters => {
  if (!applyScope) return filters;

  // Only intersect an EXISTING locations filter; applyLocationScope seeds the
  // key with the whole scope when it is absent, which is what must not happen
  // here (see above).
  const intersected = Array.isArray(filters?.locations)
    ? applyLocationScope(filters, applyScope, scopeExternalIds)
    : filters;

  // Defensive, mirroring applyLocationScope: popout-restored or otherwise
  // deserialized props can carry a plain object where a Set is expected. Treat
  // it as an empty scope (fail closed) instead of crashing.
  const ids =
    scopeExternalIds instanceof Set ? Array.from(scopeExternalIds) : [];

  return {
    ...(intersected ?? {}),
    scope_locations: ids.length > 0 ? ids : [NO_MATCH_LOCATION_ID],
  };
};
